import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Calendar, CheckCircle, AlertCircle, Clock, XCircle, Check, Ban } from 'lucide-react';
import ClientAuthService from '../services/clientAuthService';
import Modal from './ui/Modal';

const ClientQuoteModal = ({ quoteId, onClose, permissions }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadQuote();
  }, [quoteId]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const response = await ClientAuthService.getQuote(quoteId);
      setQuote(response.data[0]);
    } catch (error) {
      setError('Failed to load quote: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await ClientAuthService.downloadQuote(quoteId);
    } catch (error) {
      alert('Failed to download quote: ' + error.message);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      // Placeholder function - replace with actual API call
      console.log('Approving quote:', quoteId);
      await ClientAuthService.approveQuote(quoteId);
      
      // Update local state to reflect approval
      setQuote(prev => ({ ...prev, status: 'accepted' }));
      
      alert('Quote approved successfully!');
    } catch (error) {
      alert('Failed to approve quote: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      // Placeholder function - replace with actual API call
      console.log('Rejecting quote:', quoteId);
      await ClientAuthService.rejectQuote(quoteId);
      
      // Update local state to reflect rejection
      setQuote(prev => ({ ...prev, status: 'rejected' }));
      
      alert('Quote rejected successfully!');
    } catch (error) {
      alert('Failed to reject quote: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { class: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending' },
      'accepted': { class: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Accepted' },
      'rejected': { class: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' },
      'converted': { class: 'bg-blue-100 text-blue-800', icon: CheckCircle, text: 'Converted' },
      'expired': { class: 'bg-orange-100 text-orange-800', icon: AlertCircle, text: 'Expired' }
    };

    const config = statusConfig[status] || { class: 'bg-gray-100 text-gray-800', icon: Clock, text: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon size={14} className="mr-1" />
        {config.text}
      </span>
    );
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  const canTakeAction = (status) => {
    return status === 'pending' && !isExpired(quote?.validUntil) && permissions?.acceptQuotes;
  };

  if (loading) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Loading Quote"
        size="large"
      >
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading quote...</p>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Error"
        size="large"
        footer={
          <button 
            type="button" 
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            onClick={onClose}
          >
            Close
          </button>
        }
      >
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <div className="flex items-center">
          <FileText className="mr-2" size={20} />
          Quote {quote?.number}
        </div>
      }
      size="large"
      footer={
        <div className="flex justify-end gap-2">
          <button 
            type="button" 
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            onClick={onClose}
          >
            Close
          </button>
          {canTakeAction(quote?.status) && (
            <>
              <button 
                type="button" 
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                onClick={handleReject}
                disabled={actionLoading}
              >
                <Ban size={16} className="mr-1" />
                {actionLoading ? 'Processing...' : 'Reject'}
              </button>
              <button 
                type="button" 
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                <Check size={16} className="mr-1" />
                {actionLoading ? 'Processing...' : 'Approve'}
              </button>
            </>
          )}
          <button 
            type="button" 
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
            onClick={handleDownload}
          >
            <Download size={16} className="mr-1" />
            Download PDF
          </button>
        </div>
      }
    >
      <div>
        {quote && (
          <div>
            {/* Quote Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h6 className="text-sm font-medium text-gray-500 mb-3">Quote Details</h6>
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Quote Number:</span> {quote.number}
                  </div>
                  <div>
                    <span className="font-medium">Date:</span> {formatDate(quote.date)}
                  </div>
                  <div>
                    <span className="font-medium">Valid Until:</span> 
                    <span className={`ml-2 ${isExpired(quote.validUntil) ? 'text-red-600' : ''}`}>
                      {formatDate(quote.validUntil)}
                      {isExpired(quote.validUntil) && (
                        <small className="ml-1 text-red-600">(Expired)</small>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span> 
                    <span className="ml-2">{getStatusBadge(quote.status)}</span>
                  </div>
                </div>
              </div>
              <div>
                <h6 className="text-sm font-medium text-gray-500 mb-3">Quote Summary</h6>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span>Total Amount:</span>
                    <span className="font-semibold text-lg">{formatCurrency(quote.totalAmount)}</span>
                  </div>
                  {isExpired(quote.validUntil) && (
                    <div className="mt-2 flex items-center text-red-600">
                      <AlertCircle size={14} className="mr-1" />
                      <small>This quote has expired</small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quote Items */}
            <div className="mb-6">
              <h6 className="text-sm font-medium text-gray-500 mb-3">Quote Items</h6>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {quote.items?.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {item.itemName && (
                            <div className="font-medium text-gray-900">{item.itemName}</div>
                          )}
                          <div className="text-gray-500 text-sm">{item.description}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-900">{item.quantity}</td>
                        <td className="px-4 py-3 text-gray-900">{formatCurrency(item.price)}</td>
                        <td className="px-4 py-3 text-gray-900">{formatCurrency(item.price*item.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quote Totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div></div>
              <div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(quote.subtotal)}</span>
                    </div>
                    {quote.discount_amount > 0 && (
                      <div className="flex justify-between">
                        <span>Discount ({quote.discountPercentage}%):</span>
                        <span className="text-green-600">-{formatCurrency(quote.discountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Tax:</span>
                      <span>{formatCurrency(quote.taxAmount)}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total:</span>
                      <span>{formatCurrency(quote.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="mb-6">
                <h6 className="text-sm font-medium text-gray-500 mb-3">Notes</h6>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-700">{quote.notes}</p>
                </div>
              </div>
            )}

            {/* Validity Warning */}
            {quote.status === 'pending' && (
              <div className="mt-4">
                <div className={`rounded-md p-4 ${isExpired(quote.validUntil) ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'}`}>
                  <div className="flex items-center">
                    <Calendar size={16} className={`mr-2 ${isExpired(quote.validUntil) ? 'text-red-500' : 'text-blue-500'}`} />
                    <span className={isExpired(quote.validUntil) ? 'text-red-800' : 'text-blue-800'}>
                      {isExpired(quote.validUntil) 
                        ? 'This quote has expired and is no longer valid.'
                        : `This quote is valid until ${formatDate(quote.validUntil)}.`
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ClientQuoteModal;